import React, { Component, Suspense } from 'react';
import { compose } from 'recompose';
import classNames from 'classnames';
import { MutationFunction } from 'react-apollo';
import { Trans } from 'react-i18next';

import { Grid } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import {
  Content,
  CreateCommentComponent,
  CreateCommentReplyComponent,
  ObjectType,
  PublicUserProfile,
} from 'api';
import {
  IWithErrorHandler,
  IWithQueryParams,
  IWithUser,
  withErrorHandler,
  withQueryParams,
  withUser,
} from 'providers';
import { createCommentResponse } from 'apollo/responses';
import { contentModel } from 'apollo/reducers';
import { Editor, IEditorProps, EditorModes, Error, UserAvatar, EditorFormat } from 'components';

import editorStyles from './styles';

interface IProps {
  contentId: Content['content_id'];
  editorProps: Partial<IEditorProps>;
  onPosted: () => void;
}

interface IPropsEnhanced
  extends IProps,
    IWithErrorHandler,
    IWithQueryParams,
    IWithUser,
    WithStyles<typeof styles> {}

interface IState {
  editing: boolean;
  mentionedUsers: PublicUserProfile[];
}

const styles = (theme: Theme) =>
  createStyles({
    field: {
      flexGrow: 1,
    },
    ghost: {
      height: '100%',
      cursor: 'text',
    },
    ...editorStyles(theme),
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withErrorHandler,
  withQueryParams,
  withUser,
  withStyles(styles),
);

class Add extends Component<IPropsEnhanced, IState> {
  static getDerivedStateFromProps(props: IProps) {
    if (props.editorProps.parentId) {
      return { editing: true };
    }

    return null;
  }

  state: IState = {
    editing: false,
    mentionedUsers: [],
  };

  handleAddMention = (user: PublicUserProfile) => {
    const { mentionedUsers } = this.state;

    // Override inclusion of __typename
    const formattedUser: any = [
      {
        id: user.id,
        full_name: user.full_name,
        __typename: 'PublicUserProfile',
      },
    ];

    this.setState({ mentionedUsers: mentionedUsers.concat(formattedUser) });
  };

  toggleEditing = () => {
    this.setState({ editing: true });
  };

  onSubmit = (createCommentMutation: MutationFunction) => (value: string) => {
    const { contentId, editorProps, query, variables, onPosted, user } = this.props;
    const { mentionedUsers } = this.state;

    const commentInput = {
      comment_text: value,
      object_id: contentId,
      parent_id: editorProps.parentId,
      object_type: ObjectType.Content,
    };

    if (editorProps.parentId) {
      commentInput.parent_id = editorProps.parentId;
    }

    onPosted();

    return createCommentMutation({
      variables: commentInput,
      optimisticResponse: createCommentResponse({ commentInput, user, mentionedUsers }),
      update: (cache, result) => {
        contentModel({ contentId, cache, result, query, variables });
      },
    });
  };

  renderEditor() {
    const { classes, onError, editorProps } = this.props;
    const Mutation = editorProps.parentId ? CreateCommentReplyComponent : CreateCommentComponent;

    return (
      <Mutation onError={onError}>
        {createCommentMutation => (
          <Suspense fallback={this.renderGhost()}>
            <Editor
              focusOnMount
              submitOnEnter
              disableToolbar
              format={EditorFormat.text}
              className={classes.editor}
              placeholder={<Trans i18nKey="content:addComment" />}
              mode={EditorModes.create}
              onAddMention={this.handleAddMention}
              onSubmit={this.onSubmit(createCommentMutation)}
              {...editorProps}
            />
          </Suspense>
        )}
      </Mutation>
    );
  }

  renderGhost() {
    const { classes } = this.props;
    const draftClasses = classNames(classes.ghost, classes.editor);

    return (
      <div className={draftClasses} onClick={this.toggleEditing}>
        <div className="DraftEditor-root">
          <div className="public-DraftEditorPlaceholder-root">
            <div className="public-DraftEditorPlaceholder-inner">
              <Trans i18nKey="content:addComment" />
            </div>
          </div>
          <div className="DraftEditor-editorContainer">
            <br />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { classes, error, user, onErrorClose } = this.props;
    const { editing } = this.state;

    return (
      <Grid container spacing={1}>
        <Error key={error} message={error} onClose={onErrorClose} />
        <Grid item>
          <UserAvatar user={user} />
        </Grid>
        <Grid item xs className={classes.field}>
          {editing ? this.renderEditor() : this.renderGhost()}
        </Grid>
      </Grid>
    );
  }
}

export default enhancers(Add);
